import { parseFromValuesOrFunc } from 'blocks/cx-data-table/utils/utils'
import clsx from 'clsx'
import { type TableProps, TableRow, type TableRowProps } from 'primitives/table'
import { memo, useMemo, useRef } from 'react'
import type {
  DataTableCell,
  DataTableColumnVirtualizer,
  DataTableInstance,
  DataTableRow,
  DataTableRowData,
  DataTableRowVirtualizer,
  DataTableVirtualItem,
} from '../../types'
import { getIsRowSelected } from '../../utils/row.utils'
import { MemoTableBodyCell, TableBodyCell } from './table-body-cell'
import classes from './table-body-row.module.css'

interface Props<TData extends DataTableRowData> extends TableRowProps {
  columnVirtualizer?: DataTableColumnVirtualizer
  numRows?: number
  pinnedRowIds?: string[]
  renderedRowIndex?: number
  row: DataTableRow<TData>
  rowVirtualizer?: DataTableRowVirtualizer
  table: DataTableInstance<TData>
  tableProps: Partial<TableProps>
  virtualRow?: DataTableVirtualItem
}

export const DataTableBodyRow = <TData extends DataTableRowData>({
  columnVirtualizer,
  numRows,
  pinnedRowIds,
  renderedRowIndex = 0,
  row,
  rowVirtualizer,
  table,
  tableProps,
  virtualRow,
  ...rest
}: Props<TData>) => {
  const {
    getState,
    options: {
      enableRowOrdering,
      enableRowPinning,
      enableStickyFooter,
      enableStickyHeader,
      layoutMode,
      memoMode,
      cxTableBodyRowProps,
      renderDetailPanel,
      rowPinningDisplayMode,
    },
    refs: { tableFooterRef, tableHeadRef },
    setHoveredRow,
  } = table
  const {
    draggingColumn,
    draggingRow,
    editingCell,
    editingRow,
    hoveredRow,
    rowPinning: _,
  } = getState()

  const visibleCells = row.getVisibleCells()

  const { virtualColumns, virtualPaddingLeft, virtualPaddingRight } = columnVirtualizer ?? {}

  const isRowSelected = getIsRowSelected({ row, table })
  const isRowPinned = enableRowPinning && row.getIsPinned()
  const isRowStickyPinned = isRowPinned && rowPinningDisplayMode?.includes('sticky') && 'sticky'
  const isDraggingRow = draggingRow?.id === row.id
  const isHoveredRow = hoveredRow?.id === row.id

  const tableRowProps = {
    ...parseFromValuesOrFunc(cxTableBodyRowProps, {
      renderedRowIndex,
      row,
      table,
    }),
    ...rest,
  }

  const [bottomPinnedIndex, topPinnedIndex] = useMemo(() => {
    if (!enableRowPinning || !isRowStickyPinned || !pinnedRowIds || !row.getIsPinned()) return []
    return [[...pinnedRowIds].reverse().indexOf(row.id), pinnedRowIds.indexOf(row.id)]
  }, [enableRowPinning, isRowStickyPinned, pinnedRowIds, row])

  const tableHeadHeight = (enableStickyHeader && tableHeadRef.current?.clientHeight) || 0
  const tableFooterHeight = (enableStickyFooter && tableFooterRef.current?.clientHeight) || 0

  const rowHeight =
    // @ts-expect-error row height
    parseInt(tableRowProps?.style?.height, 10) || 53

  const handleDragEnter = () => {
    if (enableRowOrdering && draggingRow) {
      setHoveredRow(row)
    }
  }

  const rowRef = useRef<HTMLTableRowElement | null>(null)

  return (
    <TableRow
      data-dragging-row={isDraggingRow || undefined}
      data-hovered-row-target={isHoveredRow || undefined}
      data-index={renderDetailPanel ? renderedRowIndex * 2 : renderedRowIndex}
      data-row-pinned={isRowStickyPinned || isRowPinned || undefined}
      data-selected={isRowSelected || undefined}
      data-hover={String(row.original[table.options.idKey || 'id'] ?? '')}
      onDragEnter={handleDragEnter}
      ref={(node: HTMLTableRowElement) => {
        if (node) {
          rowRef.current = node
          rowVirtualizer?.measureElement(node)
        }
      }}
      {...tableRowProps}
      onClick={(e) => {
        // Handle row click from table options
        if (table.options.onRowClick) {
          table.options.onRowClick(row.original)
        }
        if (tableRowProps?.onClick) tableRowProps.onClick(e)
      }}
      onContextMenu={(e) => {
        // Handle context menu from table options
        if (table.options.onRowContextMenu) {
          table.options.onRowContextMenu(row.original, e)
        }
        if (tableRowProps?.onContextMenu) tableRowProps.onContextMenu(e)
      }}
      style={{
        // @ts-expect-error vars
        '--mrt-pinned-row-bottom':
          !virtualRow && bottomPinnedIndex !== undefined && isRowPinned
            ? `${bottomPinnedIndex * rowHeight + (enableStickyFooter ? tableFooterHeight - 1 : 0)}`
            : undefined,
        '--mrt-pinned-row-top': virtualRow
          ? undefined
          : topPinnedIndex !== undefined && isRowPinned
            ? `${topPinnedIndex * rowHeight + (enableStickyHeader ? tableHeadHeight - 1 : 0)}`
            : undefined,
        '--mrt-virtual-row-start': virtualRow ? `${virtualRow.start}` : undefined,
      }}
      className={clsx(
        classes.root,
        layoutMode?.startsWith('grid') && classes['root-grid'],
        virtualRow && classes['root-virtualized'],
        tableRowProps?.className,
      )}
    >
      {virtualPaddingLeft ? (
        <td
          className="flex"
          aria-label="Virtual padding left"
          style={{ width: virtualPaddingLeft }}
        />
      ) : null}
      {(virtualColumns ?? row.getVisibleCells()).map((cellOrVirtualCell, renderedColumnIndex) => {
        let cell = cellOrVirtualCell as DataTableCell<TData>
        if (columnVirtualizer) {
          renderedColumnIndex = (cellOrVirtualCell as DataTableVirtualItem).index
          cell = visibleCells[renderedColumnIndex]
        }
        const cellProps = {
          cell,
          numRows,
          renderedColumnIndex,
          renderedRowIndex,
          rowRef,
          table,
          virtualCell: columnVirtualizer ? (cellOrVirtualCell as DataTableVirtualItem) : undefined,
        }
        return memoMode === 'cells' &&
          cell.column.columnDef.columnDefType === 'data' &&
          !draggingColumn &&
          !draggingRow &&
          editingCell?.id !== cell.id &&
          editingRow?.id !== row.id ? (
          <MemoTableBodyCell key={cell.id} {...cellProps} />
        ) : (
          <TableBodyCell key={cell.id} {...cellProps} />
        )
      })}
      {virtualPaddingRight ? (
        <td
          className="flex"
          aria-label="Virtual padding right"
          style={{ width: virtualPaddingRight }}
        />
      ) : null}
    </TableRow>
  )
}

export const MemoDataTableBodyRow = memo(
  DataTableBodyRow,
  (prev, next) => prev.row === next.row,
) as typeof DataTableBodyRow
